import { useState } from "react";
import { Waypoint } from "react-waypoint";

import { WorkOrderListSkeleton } from "~/components/skeletons/workOrders";
import WorkOrderListCard from "~/components/tickets/WorkOrderListCard";
import { ITEMS_PER_PAGE } from "~/constants";
import { useListOwnOemTickets } from "~/services/tickets";
import type { WaypointEventType } from "~/types/waypointEvent";

interface Status {
  _id: string;
  label?: string;
  color?: string;
}

interface WorkOrder {
  _id: string;
  title?: string;
  ticketId?: string;
  ticketType?: {
    icon?: string;
    color?: string;
    isInternal?: boolean;
  };
  facility?: {
    name?: string;
  };
  assets?: { _id: string; name: string; serialNumber: string }[];
  status?: string;
  ticketChatChannels?: string[];
  ticketInternalNotesChatChannels?: string[];
  assignees?: { _id: string; name: string }[];
  dueDate?: string;
  schedule?: {
    startTime?: string | number | Date;
  };
  subWorkOrders?: any[];
  createdBy?: {
    _id: string;
  };
  archived?: boolean;
}

interface WorkOrderRowProps {
  ticket: WorkOrder;
  allStatuses: Status[];
  showArchive: boolean;
  showUnarchive: boolean;
  showDelete: boolean;
  onArchive?: (ticket: WorkOrder) => void;
  onUnarchive?: (ticket: WorkOrder) => void;
  onDelete?: (ticket: WorkOrder) => void;
  path: string;
  fromArchivedList?: boolean;
  isRootLevel?: boolean;
  showHierarchy?: boolean;
  isSelectMode?: boolean;
  isSelectedFn?: (id: string) => boolean;
  anySelected?: boolean;
  onToggleItem?: (id: string) => void;
  /** Rendered inside the archived list — sub work orders are archived too, so
   *  the child query has to opt into archived-only results. */
  isArchived?: boolean;
}

const WorkOrderRow: React.FC<WorkOrderRowProps> = ({
  ticket,
  allStatuses,
  showArchive,
  showUnarchive,
  showDelete,
  onArchive,
  onUnarchive,
  onDelete,
  path,
  fromArchivedList,
  isRootLevel = true,
  showHierarchy = false,
  isSelectMode = false,
  isSelectedFn = () => false,
  anySelected = false,
  onToggleItem = () => {},
  isArchived = false,
}) => {
  const [expandChildren, setExpandChildren] = useState(false);

  const {
    tickets: childWorkOrders = [],
    loading: childWorkOrdersLoading,
    totalCount: childWorkOrdersTotalCount,
    handleFetchMore,
  } = useListOwnOemTickets({
    where: {
      directParentId: ticket._id,
      ...(isArchived ? { archivedOnly: true } : {}),
    },
    limit: ITEMS_PER_PAGE,
    skip: 0,
    skipCondition: !expandChildren || !ticket.subWorkOrders?.length,
  });

  const handleScrollBottom = (event: WaypointEventType) => {
    if (
      childWorkOrders.length >= ITEMS_PER_PAGE &&
      !childWorkOrdersLoading &&
      event?.previousPosition !== Waypoint.above &&
      childWorkOrdersTotalCount > childWorkOrders.length
    ) {
      handleFetchMore({ limit: ITEMS_PER_PAGE, skip: childWorkOrders.length });
    }
  };

  const showExpandIcon =
    ticket.subWorkOrders && ticket.subWorkOrders.length >= 1 && showHierarchy;

  const selected = isSelectMode && isSelectedFn(ticket._id);

  const handleToggleSelect = () => {
    onToggleItem(ticket._id);
  };

  return (
    <>
      <WorkOrderListCard
        id={ticket._id}
        path={path}
        title={ticket?.title}
        ticketID={ticket?.ticketId}
        ticketType={ticket?.ticketType}
        facility={ticket?.facility?.name}
        assets={ticket?.assets}
        status={ticket?.status}
        allStatuses={allStatuses}
        ticketChannel={ticket?.ticketChatChannels?.[0]}
        ticketInternalChannel={ticket?.ticketInternalNotesChatChannels?.[0]}
        assignees={ticket?.assignees}
        dueDate={ticket?.dueDate}
        scheduleDate={ticket?.schedule}
        showArchive={showArchive && isRootLevel}
        onArchive={
          showArchive && isRootLevel ? () => onArchive?.(ticket) : undefined
        }
        showUnarchive={showUnarchive && isRootLevel}
        onUnarchive={
          showUnarchive && isRootLevel ? () => onUnarchive?.(ticket) : undefined
        }
        showDelete={showDelete && isRootLevel}
        onDelete={
          showDelete && isRootLevel ? () => onDelete?.(ticket) : undefined
        }
        fromArchivedList={fromArchivedList}
        showExpandIcon={showExpandIcon}
        isExpanded={expandChildren}
        onToggleExpand={() => setExpandChildren((prev) => !prev)}
        subWorkOrderCount={ticket.subWorkOrders?.length || 0}
        isSelectMode={isSelectMode}
        isSelected={selected}
        anySelected={anySelected}
        onToggleSelect={handleToggleSelect}
      />

      {showExpandIcon &&
        expandChildren &&
        (childWorkOrders.length > 0 ? (
          <div className="mt-sm ml-sm sm:ml-lg flex">
            <div className="w-px bg-tertiary flex-shrink-0" />
            <div className="ml-sm sm:ml-lg flex flex-col gap-sm min-w-0 flex-1">
              {childWorkOrders.map((childTicket: WorkOrder) => (
                <WorkOrderRow
                  key={childTicket._id}
                  ticket={childTicket}
                  allStatuses={allStatuses}
                  showArchive={false}
                  showUnarchive={false}
                  showDelete={false}
                  onArchive={onArchive}
                  onUnarchive={onUnarchive}
                  onDelete={onDelete}
                  path={path}
                  fromArchivedList={fromArchivedList}
                  isRootLevel={false}
                  showHierarchy={showHierarchy}
                  isSelectMode={isSelectMode}
                  isSelectedFn={isSelectedFn}
                  anySelected={anySelected}
                  onToggleItem={onToggleItem}
                  isArchived={isArchived}
                />
              ))}
              {childWorkOrdersTotalCount > childWorkOrders.length && (
                <Waypoint onEnter={handleScrollBottom} />
              )}
              {childWorkOrdersLoading && <WorkOrderListSkeleton rowCount={1} />}
            </div>
          </div>
        ) : (
          <div className="ml-2xl">
            {childWorkOrdersLoading && <WorkOrderListSkeleton rowCount={1} />}
          </div>
        ))}
    </>
  );
};

export default WorkOrderRow;
